// utils/auth/authService.ts
//import Cookies from 'js-cookie';
import config from '../../config';
import { apiHandler } from '../api/responseHandler';
import logger from '../logging/logger';
import sessionManager from '../../utils/session/sessionManager';
import { UserInfo, UNKNOWN_USER_ID } from '../../utils/auth/authTypes';
import { passwordAPI } from '../../utils/api/passwordAPI';

//Module-level variables for promise caching
let ensureUserPromise: Promise<UserInfo> | null = null;
let promiseTimestamp: number = 0;
const PROMISE_TIMEOUT = 60000; // 60 seconds

const componentName = 'authService';

// Default cookie options
const cookieOptions = {
    expires: 90,
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'strict' as const
};

interface AuthResponse {
    userId: number;
    userName: string;
    isAnonymous: boolean;
    email?: string;
    firstName?: string;
    lastName?: string;
    lastLogin?: Date | string | null;
    // Add UserType fields
    userTypeId?: number;
    isAdmin?: boolean;
    isReseller?: boolean;
    isRestricted?: boolean;
    userTypeCode?: string;
    userTypeDescription?: string;
}

/**
 * Check if a username represents an anonymous account
 */
const isAnonymousAccount = (userInfo: Partial<UserInfo>): boolean => {
    // Ensure userName exists before checking startsWith
    if (userInfo.userName && userInfo.userName.startsWith('anon_')) {
        return true;
    }

    // Return isAnonymous if explicitly set, otherwise default to true
    return userInfo.isAnonymous ?? true;
};

/**
 * Ensures a UserInfo object has all required properties including display names
 * This is now a module-private function - only accessible within this file
 */
const ensureCompleteUserInfo = (userInfo: Partial<UserInfo>): UserInfo => {
    const isAnon = userInfo?.isAnonymous || (userInfo?.userName?.startsWith('anon_') ?? false);

    // For display, strip domain from email-style usernames
    const getDisplayName = (userName: string | undefined): string => {
        if (!userName) return 'My Account';
        // If username contains @, show only the local part (before @)
        if (userName.includes('@')) {
            return userName.split('@')[0];
        }
        return userName;
    };

    return {
        userId: userInfo?.userId ?? 0,
        userName: userInfo?.userName || '',
        email: userInfo?.email || '',
        isAnonymous: isAnon,
        firstName: userInfo?.firstName || '',
        lastName: userInfo?.lastName || '',
        lastLogin: userInfo?.lastLogin || null,
        phone: userInfo?.phone || '',
        address1: userInfo?.address1 || '',
        address2: userInfo?.address2 || '',
        cityName: userInfo?.cityName || '',
        stateName: userInfo?.stateName || '',
        zipCode: userInfo?.zipCode || '',
        country: userInfo?.country || '',
        countryCode: userInfo?.countryCode || '',
        userName_DisplayMenu: isAnon ? 'Guest' : getDisplayName(userInfo?.userName),
        userName_DisplayLogin: isAnon ? '' : (userInfo?.userName || ''),

        // UserType fields with defaults
        userTypeId: userInfo?.userTypeId ?? 0,
        isAdmin: userInfo?.isAdmin || false,
        isReseller: userInfo?.isReseller || false,
        isRestricted: userInfo?.isRestricted || false,
        userTypeCode: userInfo?.userTypeCode || 'ANON_USER',
        userTypeDescription: userInfo?.userTypeDescription || 'Anonymous User',

        // Password management
        mustChangePassword: userInfo?.mustChangePassword || false
    };
};

/**
 * Clear all user data and create a new anonymous account
 */
const clearUserDataAndCreateAnonymous = async (): Promise<UserInfo> => {
    sessionManager.clearData(config.USER_COOKIE_KEY);
    sessionManager.clearData(config.IS_LOGGED_IN_KEY);
    sessionManager.clearData(config.LAST_ENSURE_USER_KEY);
    return await authService.createAnonymousAccount();
};

/**
 * Central authentication service to handle all auth-related operations
 */
export const authService = {
    isAnonymousAccount,

    /**
 * Sets the display names for a user based on their anonymous status
 */
    setUserDisplayNames: (userInfo: UserInfo): UserInfo => {
        const isAnon = userInfo.isAnonymous || (userInfo.userName?.startsWith('anon_') ?? false);


        return {
            ...userInfo,
            isAnonymous: isAnon, // Ensure isAnonymous is correctly set
            userName_DisplayMenu: isAnon ? 'Guest' : (userInfo.userName || 'My Account'),
            userName_DisplayLogin: isAnon ? '' : userInfo.userName // Empty for anon users in login form
        };
    },

    /**
     * Save user info to cookies - single source of truth for user data
     */
    saveUserInfoToCookie: (userInfo: Partial<UserInfo>, forceSave: boolean = false): UserInfo => {
        // Validate the userInfo object first
        if (!userInfo) {
            console.error('Invalid userInfo passed to saveUserInfo');
            return ensureCompleteUserInfo({});
        }

        logger.log('LogAuth', componentName, 'saveUserInfoToCookie - Provided userInfo:', {
            userId: userInfo.userId ?? UNKNOWN_USER_ID,
            ...userInfo
        });

        // Ensure all properties are present
        const completeUserInfo = ensureCompleteUserInfo(userInfo);

        // Log the user info being saved
        logger.log('LogAuth', componentName, 'saveUserInfoToCookie - Provided userInfo:', {
            userId: userInfo.userId ?? UNKNOWN_USER_ID,
            userName: userInfo.userName,
            isAnonymous: userInfo.isAnonymous,
            // include only key fields you want to log
        });


        //Don't overwrite a valid user cookie with an anon... user
        const cookieUserInfo = sessionManager.retrieveData<UserInfo>(config.USER_COOKIE_KEY);

        logger.log('LogAuth', componentName, 'saveUserInfoToCookie - existing cookieUserInfo:', {
            ...cookieUserInfo,
            userId: cookieUserInfo?.userId ?? UNKNOWN_USER_ID
        });


        let shouldOverwriteWithAnon =
            !cookieUserInfo ||
            cookieUserInfo.isAnonymous ||
            cookieUserInfo.userName?.startsWith('anon_');

        if (forceSave === true) { shouldOverwriteWithAnon = true }

        if (completeUserInfo.isAnonymous) {
            //Only write over null return or another anon user cookie
            if (shouldOverwriteWithAnon === true) {
                logger.log('LogAuth', componentName, 'saveUserInfoToCookie - Overwriting... completeUserInfo.isAnonymous: true, shouldOverwriteWithAnon:', {
                    userId: completeUserInfo.userId ?? UNKNOWN_USER_ID,
                    shouldOverwriteWithAnon
                });

                sessionManager.storeData(config.USER_COOKIE_KEY, completeUserInfo);
            }
            else {
                logger.log('LogAuth', componentName, 'saveUserInfoToCookie - Skipping overwrite... completeUserInfo.isAnonymous: true, shouldOverwriteWithAnon:', {
                    userId: completeUserInfo.userId || UNKNOWN_USER_ID,
                    shouldOverwriteWithAnon
                });
            }
        }
        else {
            logger.log('LogAuth', componentName, 'saveUserInfoToCookie - Overwriting... completeUserInfo.isAnonymous:', {
                userId: completeUserInfo.userId || UNKNOWN_USER_ID,
                isAnonymous: completeUserInfo.isAnonymous
            });

            // Always save to session storage
            sessionManager.storeData(config.USER_COOKIE_KEY, completeUserInfo);
        }

        return completeUserInfo;
    },

    /**
     * Get the current user information from cookies
     */
    // In authService.ts - getCurrentUserFromCookie function
    getCurrentUserFromCookie: (): UserInfo => {
        // First: try the cookie (normal path)
        const cookieUser = sessionManager.retrieveData<UserInfo>(config.USER_COOKIE_KEY);
        if (cookieUser && cookieUser.userId && cookieUser.userId > 0) {
            return ensureCompleteUserInfo(cookieUser);
        }

        // Second: fall back to localStorage anonymous user (persists across sessions)
        const anonUser = sessionManager.retrieveAnonymousUser();
        if (anonUser && anonUser.userId > 0) {
            logger.log('LogAuth', componentName, 'Cookie missing, recovered userId from localStorage', {
                userId: anonUser.userId, userName: anonUser.userName
            });
            return ensureCompleteUserInfo({
                userId: anonUser.userId,
                userName: anonUser.userName,
                email: '',
                isAnonymous: true
            });
        }

        // Final fallback: truly new user
        return ensureCompleteUserInfo({
            userId: 0, userName: '', email: '', isAnonymous: true
        });
    },

    /**
     * Get user's IP address using multiple methods with fallbacks
     * @returns Promise<string> IP address or empty string if all methods fail
     */
    getUserIPAddress: async (): Promise<string> => {
        const ipServices = [
            { url: 'https://api.ipify.org?format=json', parser: (data: any) => data.ip },
            { url: 'https://ipapi.co/json/', parser: (data: any) => data.ip },
            { url: 'https://icanhazip.com/', parser: (text: string) => text.trim() },
            { url: 'https://httpbin.org/ip', parser: (data: any) => data.origin.split(',')[0].trim() }
        ];

        for (const service of ipServices) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);
                
                const response = await fetch(service.url, {
                    method: 'GET',
                    signal: controller.signal
                });
                
                clearTimeout(timeoutId);
                
                if (response.ok) {
                    const contentType = response.headers.get('content-type');
                    let result;
                    
                    if (contentType && contentType.includes('application/json')) {
                        const data = await response.json();
                        result = service.parser(data);
                    } else {
                        const text = await response.text();
                        result = service.parser(text);
                    }
                    
                    // Validate IP format (basic IPv4 check)
                    if (result && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(result)) {
                        logger.log('LogAuth', componentName, 'Successfully retrieved IP address', { 
                            service: service.url, 
                            ip: result.substring(0, 7) + '...' // Log partial IP for privacy 
                        });
                        return result;
                    }
                }
            } catch (error) {
                logger.log('LogAuth', componentName, 'IP service failed', { 
                    service: service.url, 
                    error: error instanceof Error ? error.message : 'Unknown error' 
                });
                continue;
            }
        }
        
        logger.log('LogAuth', componentName, 'All IP services failed, will use timestamp fallback');
        return '';
    },

    /**
     * Generate a random anonymous username
     * Checks localStorage first to reuse existing anonymous accounts.
     * Uses timestamp-based username for new users (no external IP lookup).
     */
    generateAnonymousUsername: async (): Promise<string> => {
        // Check localStorage for existing anonymous user first
        const existingAnonUser = sessionManager.retrieveAnonymousUser();
        if (existingAnonUser) {
            logger.log('LogAuth', componentName, 'Reusing existing anonymous username from localStorage', {
                userName: existingAnonUser.userName,
                userId: existingAnonUser.userId,
                created: new Date(existingAnonUser.created).toISOString()
            });
            return existingAnonUser.userName;
        }

        // Generate timestamp-based username immediately (no external IP lookup)
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const username = `anon_${timestamp}_${randomString}`;

        logger.log('LogAuth', componentName, 'Generated timestamp-based anonymous username', {
            usernameLength: username.length
        });

        return username;
    },

    // Update the checkUsernameAvailability function to accept an optional userId
    checkUsernameAvailability: async (userName: string, userId: number = 0): Promise<number> => {
        try {
            logger.log('LogAuth', componentName, 'Checking username availability', {
                userName,
                userId: userId || UNKNOWN_USER_ID
            });

            const response = await apiHandler.fetch<{ userId: number }>(
                `${config.API_URL}/api/user/verify-username`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        userName,
                        userId  // Include userId in the request
                    })
                }
            );

            if (response.error) throw response.error;

            logger.log('LogAuth', componentName, 'Username check response', {
                responseUserId: response.data?.userId || UNKNOWN_USER_ID,
                requestedUserId: userId || UNKNOWN_USER_ID
            });

            return response.data?.userId ?? 0;
        } catch (err) {
            console.error('Failed to check username availability:', err);
            throw err;
        }
    },

    /**
     * Convert an anonymous account to a permanent one
     */
    convertAnonymousAccount: async (
        userId: number,
        newUserName: string,
        email: string,
        firstName: string,
        lastName: string,
        password: string,
        phone?: string,
        city?: string,
        state?: string,
        zipCode?: string,
        country?: string,
        address1?: string,
        address2?: string
    ): Promise<boolean> => {
        try {
            logger.log('LogAuth', componentName, 'convertAnonymousAccount Starting account conversion', {
                userId: userId || UNKNOWN_USER_ID,
                newUserName,
                email
            });

            // First verify/reserve the username
            const responseUserId = await authService.checkUsernameAvailability(newUserName, userId);

            // If the username/email is taken by someone else (including if it's already a registered account)
            if (responseUserId === 0) {
                logger.log('LogAuth', componentName, 'convertAnonymousAccount Email is already registered to another account', {
                    email,
                    requestUserId: userId || UNKNOWN_USER_ID
                });
                throw new Error('This email address is already registered. Please log in with your existing account or use a different email.');
            }

            // Log the response for debugging
            logger.log('LogAuth', componentName, 'convertAnonymousAccount Username check response:', {
                responseUserId: responseUserId,
                requestUserId: userId || UNKNOWN_USER_ID,
                userName: newUserName
            });

            // If we get here, the username is available or belongs to this user
            if (responseUserId !== userId) {
                logger.logError('LogAuth', componentName, 'convertAnonymousAccount Username verification returned unexpected userId', {
                    requestUserId: userId || UNKNOWN_USER_ID,
                    responseUserId: responseUserId
                });
                throw new Error('Account verification failed. Please try again.');
            }

            // If the username is available, we can continue
            logger.log('LogAuth', componentName, 'convertAnonymousAccount Username is available for conversion', {
                responseUserId,
                newUserName
            });

            // Step 1: Update the user record with profile info
            let profileResponse;
            try {
                profileResponse = await apiHandler.fetch<{ updatedUserId: number }>(
                    `${config.API_URL}/api/user/${responseUserId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                            userName: newUserName,
                            firstName,
                            lastName,
                            email,
                            phone: phone || null,
                            address1: address1 || null,
                            address2: address2 || null,
                            cityName: city || null,
                            stateName: state || null,
                            zipCode: zipCode || null,
                            notes: null,
                            discountCodeId: 0,
                            country: country || null
                        })
                    }
                );
            } catch (error: any) {
                // Handle duplicate email/username errors from database constraints
                if (error.status === 409) {
                    if (error.data?.code === 'DUPLICATE_EMAIL') {
                        logger.logError('LogAuth', componentName, 'Email already registered', { email });
                        throw new Error('This email is already registered. Please log in instead.');
                    }
                    if (error.data?.code === 'DUPLICATE_USERNAME') {
                        logger.logError('LogAuth', componentName, 'Username already taken', { userName: newUserName });
                        throw new Error('This username is already taken. Please choose another.');
                    }
                }
                throw error;
            }

            if (profileResponse.error) throw profileResponse.error;

            logger.log('LogAuth', componentName, 'convertAnonymousAccount Profile update response', {
                updatedUserId: profileResponse.data?.updatedUserId
            });

            if (profileResponse.data?.updatedUserId === undefined || profileResponse.data.updatedUserId < 1) {
                logger.logError('LogError', componentName, 'convertAnonymousAccount Profile update failed during conversion', {
                    updatedUserId: profileResponse.data?.updatedUserId
                });
                throw new Error('Failed to update profile information');
            }

            // Step 2: Set the password ONLY if provided (skip for guest checkout)
            if (password && password.length > 0) {
                logger.log('LogAuth', componentName, 'convertAnonymousAccount Setting password for full account', {
                    originalUserId: userId,
                    responseUserId: responseUserId,
                    updatedUserId: profileResponse.data?.updatedUserId
                });

                // Use responseUserId which is the validated user ID from username verification
                const passwordSuccess = await passwordAPI.setPassword(responseUserId, password);

                if (!passwordSuccess) {
                    throw new Error('Failed to set password during conversion');
                }

                logger.log('LogAuth', componentName, 'convertAnonymousAccount Password set for full account', {
                    updatedUserId: profileResponse.data?.updatedUserId
                });
            } else {
                logger.log('LogAuth', componentName, 'convertAnonymousAccount Skipping password for guest checkout (UserTypeId = 5)', {
                    originalUserId: userId,
                    responseUserId: responseUserId,
                    updatedUserId: profileResponse.data?.updatedUserId
                });
            }

            // Ensure the new user info is saved correctly
            const newUser = {
                userId: responseUserId,
                userName: newUserName,
                email: email || '',
                isAnonymous: false,
                firstName,
                lastName
            };

            // Update cookies with new data
            logger.log('LogAuth', componentName, 'convertAnonymousAccount Saving converted user info to cookie', newUser);
            authService.saveUserInfoToCookie(newUser);

            // Set as logged in
            sessionManager.storeData(config.IS_LOGGED_IN_KEY, 'true');
            
            // NEW: Clear anonymous user from localStorage since they're now a real user
            sessionManager.clearAnonymousUser();

            return true;
        } catch (err) {
            logger.logError('LogAuth', componentName, 'convertAnonymousAccount Failed to convert anonymous err:', err);
            console.error('Failed to convert anonymous account:', err);
            throw err;
        }
    },

    /**
     * Authenticate a user with username/email and password
     */
    login: async (userNameOrEmail: string, password: string): Promise<UserInfo> => {
        try {
            const authResponse = await passwordAPI.login(userNameOrEmail, password);

            // Create user object with UserType fields, providing defaults for optional properties
            const userPartial = {
                userId: authResponse.userId,
                userName: authResponse.userName,
                email: authResponse.email || '',
                isAnonymous: authResponse.isAnonymous,
                firstName: authResponse.firstName,
                lastName: authResponse.lastName,
                userTypeId: authResponse.userTypeId ?? 0,
                isAdmin: authResponse.isAdmin || false,
                isReseller: authResponse.isReseller || false,
                isRestricted: authResponse.isRestricted || false,
                userTypeCode: authResponse.userTypeCode || 'WEB_USER',
                userTypeDescription: authResponse.userTypeDescription || 'Web User',
                mustChangePassword: authResponse.mustChangePassword || false
            };

            const user = ensureCompleteUserInfo(userPartial);
            authService.saveUserInfoToCookie(user);
            sessionManager.storeData(config.IS_LOGGED_IN_KEY, 'true');

            return user;
        } catch (err) {
            console.error('Login failed:', err);
            throw err;
        }
    },


    /**
 * Log the user out - enhanced version
 */
    logout: (): void => {
        // Clear promise cache to prevent re-authentication
        ensureUserPromise = null;
        promiseTimestamp = 0;

        // Use sessionManager to clear all auth-related data
        sessionManager.clearData(config.IS_LOGGED_IN_KEY);
        sessionManager.clearData(config.USER_COOKIE_KEY);
        sessionManager.clearData(config.LAST_ENSURE_USER_KEY);

        // Clear any other auth-related session data
        sessionManager.clearData('lost_user_info');
        sessionManager.clearData('profile_backup');
        sessionManager.clearData('account_just_converted');
        
        // NEW: Clear anonymous user from localStorage
        sessionManager.clearAnonymousUser();

        // Force clear all cookies with the prefix
        sessionManager.endSession();

        // Log the logout action
        logger.log('LogAuth', componentName, 'User logout completed - all data cleared', {
            userId: UNKNOWN_USER_ID
        });
    },

    /**
     * Check if the user is logged in
     */
    isLoggedIn: (): boolean => {
        //return Cookies.get(IS_LOGGED_IN_COOKIE) === 'true';
        return sessionManager.retrieveData(config.IS_LOGGED_IN_KEY) === 'true';
    },

    /**
 * Create an anonymous user account
 * NOW CHECKS LOCALSTORAGE FIRST to reuse existing anonymous accounts
 */
    createAnonymousAccount: async (): Promise<UserInfo> => {
        logger.log('LogAuth', componentName, 'Creating anonymous account', {
            userId: UNKNOWN_USER_ID
        });

        // NEW: Check localStorage for existing anonymous user
        const existingAnonUser = sessionManager.retrieveAnonymousUser();
        if (existingAnonUser) {
            logger.log('LogAuth', componentName, 'Found existing anonymous user in localStorage', {
                userId: existingAnonUser.userId,
                userName: existingAnonUser.userName
            });
            
            // Verify the user still exists on the server
            try {
                const verifyResponse = await apiHandler.fetch<any>(
                    `${config.API_URL}/api/user/${existingAnonUser.userId}`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        timeout: 5000
                    }
                );
                
                if (verifyResponse.data && verifyResponse.data.UserId > 0) {
                    logger.log('LogAuth', componentName, 'Existing anonymous user verified on server, reusing');
                    const userInfo = ensureCompleteUserInfo({
                        userId: existingAnonUser.userId,
                        userName: existingAnonUser.userName,
                        email: '',
                        isAnonymous: true,
                        firstName: 'Anonymous',
                        lastName: 'User'
                    });
                    authService.saveUserInfoToCookie(userInfo);
                    sessionManager.storeData(config.IS_LOGGED_IN_KEY, 'true');
                    return userInfo;
                }
            } catch (verifyError) {
                logger.log('LogWarning', componentName, 'Existing anonymous user not found on server, creating new one');
                sessionManager.clearAnonymousUser();
            }
        }

        // Generate an anonymous username (will return existing from localStorage if available)
        const userName = await authService.generateAnonymousUsername();
        try {
            // First verify/reserve the username by calling our function instead of direct API
            const userId = await authService.checkUsernameAvailability(userName);

            if (userId === 0) {
                logger.log('LogAuth', componentName, 'Generated username is not available, which is unexpected', {
                    userId: userId || UNKNOWN_USER_ID
                });
                throw new Error('Generated username is not available');
            }

            // Now create/update the user record updateStatus is the new userId but it does not appear to be used
            // Note: We must provide firstName and lastName as they're required by the endpoint
            const createResponse = await apiHandler.fetch<{ updatedUserId: number }>(
                `${config.API_URL}/api/user/${userId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        userName: userName,
                        firstName: 'Anonymous', // Required field
                        lastName: 'User',       // Required field
                        email: '',
                        phone: null,
                        address1: null,
                        address2: null,
                        cityName: null,
                        stateName: null,
                        zipCode: null,
                        notes: null,
                        discountCodeId: 0,
                        country: null
                    }),
                    timeout: 8000,
                    retries: 1
                }
            );

            const newUserId = createResponse.data?.updatedUserId;

            logger.log('LogAuth', componentName, 'createAnonymousAccount API response:', {
                newUserId: newUserId || UNKNOWN_USER_ID
            });

            if (createResponse.error) throw createResponse.error;
            
            // Validate that we got a userId back
            if (!newUserId || newUserId <= 0) {
                throw new Error('Failed to get valid userId from createAnonymousAccount');
            }

            // Create the user object with the assigned user ID
            const newUser = ensureCompleteUserInfo({
                //userId: userId,
                userId: newUserId,
                userName: userName,
                email: '',
                isAnonymous: true,
                firstName: 'Anonymous',
                lastName: 'User'
            });

            // Save to cookie and mark as logged in
            authService.saveUserInfoToCookie(newUser);
            sessionManager.storeData(config.IS_LOGGED_IN_KEY, 'true');
            
            // NEW: Save anonymous user to localStorage for persistence across sessions
            sessionManager.storeAnonymousUser(newUserId, userName, 90); // 90 days expiry

            logger.log('LogAuth', componentName, 'Anonymous account created and saved to localStorage:', newUser);
            return newUser;
        } catch (err) {
            logger.log('LogError', componentName, 'Failed to create anonymous account:', err);
            // If the server call fails, return a default anonymous user
            return ensureCompleteUserInfo({
                userId: 0,
                userName: userName,
                email: '',
                isAnonymous: true
            });
        }
    },

    //!!!!!!!!!!!!!  This is the ONE place where user information should be validated !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    ensureUser: async (): Promise<UserInfo> => {
        logger.logWithTrace('LogAuth', componentName, 'ensureUser called', { userId: UNKNOWN_USER_ID });

        // 1. PROMISE CACHING - return existing promise if still valid
        if (ensureUserPromise && (Date.now() - promiseTimestamp) < PROMISE_TIMEOUT) {
            logger.log('LogAuth', componentName, 'Using existing ensureUser promise');
            return ensureUserPromise;
        }

        // 2. CREATE NEW PROMISE with graceful fallback wrapper
        const newPromise = (async (): Promise<UserInfo> => {
            try {
                const cookieUser = authService.getCurrentUserFromCookie();

                if (cookieUser.userId === 0) {
                    return await authService.createAnonymousAccount();
                }

                if (cookieUser.userId > 0) {
                    try {
                        const response = await apiHandler.fetch<any>(
                            `${config.API_URL}/api/user/${cookieUser.userId}`,
                            {
                                method: 'GET',
                                credentials: 'include',
                                timeout: 10000
                            }
                        );

                        if (response.data && response.data.UserId && response.data.UserId > 0) {
                            // Map database field names to UserInfo properties
                            const userPartial = {
                                userId: response.data.UserId,
                                userName: response.data.UserName,
                                email: response.data.Email || '',
                                isAnonymous: response.data.IsAnonymous,
                                firstName: response.data.FirstName,
                                lastName: response.data.LastName,
                                lastLogin: response.data.LastLogin,
                                phone: response.data.Phone || '',
                                address1: response.data.Address1 || '',
                                address2: response.data.Address2 || '',
                                cityName: response.data.CityName || '',
                                stateName: response.data.StateName || '',
                                zipCode: response.data.ZipCode || '',
                                country: response.data.Country || '',
                                countryCode: response.data.CountryCode || '',
                                userTypeId: response.data.UserTypeId,
                                isAdmin: response.data.IsAdmin,
                                isReseller: response.data.IsReseller,
                                isRestricted: response.data.IsRestricted,
                                userTypeCode: response.data.UserTypeCode,
                                userTypeDescription: response.data.UserTypeDescription
                            };

                            const user = ensureCompleteUserInfo(userPartial);
                            authService.saveUserInfoToCookie(user);
                            return user;
                        } else {
                            return await clearUserDataAndCreateAnonymous();
                        }
                    } catch (validationError) {
                        return await clearUserDataAndCreateAnonymous();
                    }
                }

                return await authService.createAnonymousAccount();

            } catch (error) {
                try {
                    return await authService.createAnonymousAccount();
                } catch (createError) {
                    return ensureCompleteUserInfo({ userId: 0, isAnonymous: true });
                }
            }
        })();

        // Store promise and timestamp
        ensureUserPromise = newPromise;
        promiseTimestamp = Date.now();

        // Clear promise after timeout
        setTimeout(() => {
            if (promiseTimestamp !== 0 && (Date.now() - promiseTimestamp) >= PROMISE_TIMEOUT) {
                ensureUserPromise = null;
            }
        }, PROMISE_TIMEOUT);

        return newPromise;
    },

};